import React from "react";
import styled from "styled-components";
import { LessonAward } from "../../common/api";
import { Account, AccountContext } from "../../common/context/AccountContext";
import { ApiContext } from "../../common/context/ApiContext";
import { useGridColumnCount, useLiveData } from "../../common/utils";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import {
  AwardModal,
  AwardSticker,
  STICKER_PLACEHOLDER_URL,
} from "../../components/AwardSticker";
import { default as UUID } from "uuid";
import { useTranslation } from "react-i18next";
import { COLOR_TEXT_SECONDARY, COLOR_WHITE } from "../../common/colors";
import { SPACE_XL, SPACE_2XL } from "../../common/spacing";

const STICKER_MAX_WIDTH = 130;
const MIN_PLACEHOLDER_ROWS = 2;

// Fills out the last row plus one more, or two rows when nothing is unlocked.
const placeholderCount = (unlocked: number, columns: number) => {
  if (unlocked === 0) {
    return MIN_PLACEHOLDER_ROWS * columns;
  }
  const remainder = unlocked % columns;
  return remainder === 0 ? columns : columns - remainder + columns;
};

type AwardsState = {
  unlockedAwards: LessonAward[] | null;
};

const StickerGrid = styled.div<{ $columns: number }>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns}, minmax(0, 1fr));
  gap: ${SPACE_2XL}px;
  padding: ${SPACE_XL}px 0;

  @media (max-width: 1100px) {
    gap: ${SPACE_XL}px;
  }
`;

const StickerPlaceholder = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
`;

// Fades out the trailing placeholder rows behind the empty-state hint.
const Scrim = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 194px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACE_2XL}px 0;
  background: linear-gradient(to top, ${COLOR_WHITE}, rgba(255, 255, 255, 0));
  color: ${COLOR_TEXT_SECONDARY};
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
`;

// Earned stickers, padded with placeholders to fill the row plus one more.
export const EarnStickersTab: React.FC<{
  onCountChange?: (count: number) => void;
  showEmptyMessage?: boolean;
}> = ({ onCountChange, showEmptyMessage }) => {
  const [state, setState] = React.useState<AwardsState>({
    unlockedAwards: null,
  });
  const [, setAccount] = React.useState<Account>();
  const [modalAward, setModalAward] = React.useState<LessonAward | null>(null);

  const { accountLiveData } = React.useContext(AccountContext);
  const { getAwardsMetadata } = React.useContext(ApiContext);
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const { t } = useTranslation();
  const [grid, setGrid] = React.useState<HTMLDivElement | null>(null);
  const columns = Math.max(
    1,
    useGridColumnCount(grid, STICKER_MAX_WIDTH, undefined, { fit: "max" })
  );

  const awardsFetchedForProfileStateVersion = React.useRef<number | null>(null);
  const fetchJobForProfileStateVersion = React.useRef<number | null>(null);
  const currentFetchJobUuid = React.useRef<string | null>(null);
  const timeoutId = React.useRef<number>();

  const maybeRefetchAwards = React.useCallback(
    async (account: Account) => {
      const profile = account.displayProfile();
      if (profile === null) {
        currentFetchJobUuid.current = null;
        awardsFetchedForProfileStateVersion.current = null;
        fetchJobForProfileStateVersion.current = null;
        setState({ unlockedAwards: [] });
        stopLoading();
        return;
      }
      if (account.hasMaybeProfileState()) {
        setAccount(account);
        if (
          account.profileStateCurrentVersion !==
            awardsFetchedForProfileStateVersion.current &&
          account.profileStateCurrentVersion !==
            fetchJobForProfileStateVersion.current
        ) {
          startLoading();
          const fetchJobUuid = (currentFetchJobUuid.current = UUID.v4());
          fetchJobForProfileStateVersion.current =
            account.profileStateCurrentVersion;
          const awardsMetadata = await getAwardsMetadata(profile.id);
          if (currentFetchJobUuid.current !== fetchJobUuid) {
            return;
          }
          setState({ unlockedAwards: awardsMetadata.unlocked_awards });
          awardsFetchedForProfileStateVersion.current =
            fetchJobForProfileStateVersion.current;
          stopLoading();
          timeoutId.current = window.setTimeout(
            () => {
              currentFetchJobUuid.current = UUID.v4();
              awardsFetchedForProfileStateVersion.current = null;
              fetchJobForProfileStateVersion.current = null;
              maybeRefetchAwards(accountLiveData.value);
            },
            8 * 60 * 60 * 1000
          );
        }
      }
    },
    [accountLiveData, getAwardsMetadata, startLoading, stopLoading]
  );

  useLiveData(
    React.useCallback(
      (account: Account) => {
        maybeRefetchAwards(account);
      },
      [maybeRefetchAwards]
    ),
    accountLiveData
  );

  const unlockedAwardCount = state.unlockedAwards?.length;
  React.useEffect(() => {
    if (unlockedAwardCount !== undefined) {
      onCountChange?.(unlockedAwardCount);
    }
  }, [onCountChange, unlockedAwardCount]);

  if (!state.unlockedAwards) {
    return <></>;
  }

  return (
    <>
      <StickerGrid ref={setGrid} $columns={columns}>
        {state.unlockedAwards.map((award) => (
          <AwardSticker
            key={award.uuid}
            award={award}
            onClick={() => setModalAward(award)}
          />
        ))}
        {Array.from(
          { length: placeholderCount(state.unlockedAwards.length, columns) },
          (_, i) => (
            <StickerPlaceholder
              key={`placeholder-${i}`}
              alt=""
              src={STICKER_PLACEHOLDER_URL}
            />
          )
        )}
        {showEmptyMessage && <Scrim>{t("profile_stickers_empty")}</Scrim>}
      </StickerGrid>

      <AwardModal award={modalAward} onClose={() => setModalAward(null)} />
    </>
  );
};
